import type { UploadedFile } from "@taxassist/api-client";
import { fetchAuthSession } from "aws-amplify/auth";
import { appConfig } from "../config/environment";

/**
 * Error class for file preview operations
 */
export class FilePreviewError extends Error {
  public readonly statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "FilePreviewError";
    this.statusCode = statusCode;
  }
}

/**
 * Get a presigned URL for file preview
 */
export const getFilePreviewUrl = async (
  file: UploadedFile,
): Promise<string> => {
  try {
    // Get auth token from Cognito session
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FilePreviewError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    // Request presigned URL for preview
    const response = await fetch(
      `${appConfig.api.baseUrl}/files/preview?s3Key=${encodeURIComponent(file.s3Key)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FilePreviewError(
        errorData.message || `Failed to get preview URL (${response.status})`,
        response.status,
      );
    }

    const result = await response.json();

    if (!result.previewUrl) {
      throw new FilePreviewError("No preview URL in response");
    }

    return result.previewUrl;
  } catch (error) {
    if (error instanceof FilePreviewError) {
      throw error;
    }

    // Handle network errors or other unexpected errors
    throw new FilePreviewError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while getting preview URL",
    );
  }
};

/**
 * Unified cache for all presigned URLs (preview and download) to avoid multiple requests
 */
const urlCache = new Map<string, { url: string; expires: number }>();

/**
 * Get a cached presigned URL or fetch a new one
 */
export const getCachedFilePreviewUrl = async (
  file: UploadedFile,
): Promise<string> => {
  const cacheKey = `preview-${file.s3Key}`;
  const now = Date.now();

  // Check if we have a valid cached URL
  const cached = urlCache.get(cacheKey);
  if (cached && cached.expires > now) {
    return cached.url;
  }

  // Fetch new URL
  const url = await getFilePreviewUrl(file);

  // Cache for 50 minutes (URLs typically expire in 60 minutes)
  const expires = now + 50 * 60 * 1000;
  urlCache.set(cacheKey, { url, expires });

  return url;
};

/**
 * Clear expired entries from the unified URL cache
 */
export const cleanupUrlCache = () => {
  const now = Date.now();
  for (const [key, value] of urlCache.entries()) {
    if (value.expires <= now) {
      urlCache.delete(key);
    }
  }
};

/**
 * Clear all entries from the unified URL cache (for testing)
 */
export const clearUrlCache = () => {
  urlCache.clear();
};

/**
 * Get a presigned URL for file download with proper download headers
 */
export const getFileDownloadUrl = async (
  file: UploadedFile,
  firmId: string,
  jobId: string,
  isProcessingResult: boolean = false,
): Promise<string> => {
  try {
    // Get auth token from Cognito session
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FilePreviewError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    // Determine the endpoint based on file type
    const endpoint = isProcessingResult
      ? `/firms/${firmId}/jobs/${jobId}/processing-results/download`
      : `/firms/${firmId}/jobs/${jobId}/files/download`;

    // Request presigned URL for download
    const response = await fetch(
      `${appConfig.api.baseUrl}${endpoint}?s3Key=${encodeURIComponent(file.s3Key)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FilePreviewError(
        errorData.message || `Failed to get download URL (${response.status})`,
        response.status,
      );
    }

    const result = await response.json();

    if (!result.downloadUrl) {
      throw new FilePreviewError("No download URL in response");
    }

    return result.downloadUrl;
  } catch (error) {
    if (error instanceof FilePreviewError) {
      throw error;
    }

    // Handle network errors or other unexpected errors
    throw new FilePreviewError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while getting download URL",
    );
  }
};

/**
 * Get a cached download URL or fetch a new one
 */
export const getCachedFileDownloadUrl = async (
  file: UploadedFile,
  firmId: string,
  jobId: string,
  isProcessingResult: boolean = false,
): Promise<string> => {
  const cacheKey = `download-${file.s3Key}-${isProcessingResult ? "processing" : "uploaded"}`;
  const now = Date.now();

  // Check if we have a valid cached URL
  const cached = urlCache.get(cacheKey);
  if (cached && cached.expires > now) {
    return cached.url;
  }

  // Fetch new URL
  const url = await getFileDownloadUrl(file, firmId, jobId, isProcessingResult);

  // Cache for 50 minutes (URLs typically expire in 60 minutes)
  const expires = now + 50 * 60 * 1000;
  urlCache.set(cacheKey, { url, expires });

  return url;
};

/**
 * Response type for download all processing results
 */
export interface DownloadAllResponse {
  downloadUrl: string;
  fileName: string;
  expiresIn: number;
  totalFiles: number;
  message: string;
}

/**
 * Download all processing results as a zip file and trigger browser download
 */
export const downloadAllProcessingResults = async (
  jobId: string,
): Promise<DownloadAllResponse> => {
  try {
    // Get auth token from Cognito session
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FilePreviewError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    // Request zip creation and download URL
    const response = await fetch(
      `${appConfig.api.baseUrl}/jobs/${jobId}/processing-results/download-all`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FilePreviewError(
        errorData.message || `Failed to create zip file (${response.status})`,
        response.status,
      );
    }

    const result = await response.json();

    if (!result.downloadUrl) {
      throw new FilePreviewError("No download URL in response");
    }

    return {
      downloadUrl: result.downloadUrl,
      fileName: result.fileName || `job_${jobId}_processing_results.zip`,
      expiresIn: result.expiresIn || 3600,
      totalFiles: result.totalFiles || 0,
      message: result.message || "Files ready for download",
    };
  } catch (error) {
    if (error instanceof FilePreviewError) {
      throw error;
    }

    // Handle network errors or other unexpected errors
    throw new FilePreviewError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while creating zip file",
    );
  }
};

/**
 * Download all uploaded source files as a zip file
 * Used by ops reviewers to download source files for manual processing
 */
export const downloadAllSourceFiles = async (
  jobId: string,
): Promise<DownloadAllResponse> => {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FilePreviewError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    const response = await fetch(
      `${appConfig.api.baseUrl}/ops/jobs/${encodeURIComponent(jobId)}/source-files/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FilePreviewError(
        errorData.message || `Failed to create zip file (${response.status})`,
        response.status,
      );
    }

    const result = await response.json();

    if (!result.downloadUrl) {
      throw new FilePreviewError("No download URL in response");
    }

    return {
      downloadUrl: result.downloadUrl,
      fileName: result.fileName || `job_${jobId}_source_files.zip`,
      expiresIn: result.expiresIn || 3600,
      totalFiles: result.totalFiles || 0,
      message: result.message || "Files ready for download",
    };
  } catch (error) {
    if (error instanceof FilePreviewError) {
      throw error;
    }

    throw new FilePreviewError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while creating zip file",
    );
  }
};

/**
 * Download K1X files as a zip file and trigger browser download
 * Called automatically when job moves to processing state
 */
export const downloadK1xFiles = async (jobId: string): Promise<void> => {
  try {
    // Get auth token from Cognito session
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FilePreviewError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    // Request K1X zip creation and download URL
    const response = await fetch(
      `${appConfig.api.baseUrl}/ops/jobs/${jobId}/k1x-files/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FilePreviewError(
        errorData.message ||
          `Failed to create K1X zip file (${response.status})`,
        response.status,
      );
    }

    const result = await response.json();

    if (!result.downloadUrl) {
      throw new FilePreviewError("No download URL in response");
    }

    // Trigger download by creating an anchor and clicking it
    const anchor = document.createElement("a");
    anchor.style.display = "none";
    anchor.href = result.downloadUrl;
    anchor.download = result.fileName || `k1x_files_${jobId}.zip`;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    console.log(`K1X files download initiated: ${result.fileName}`);
  } catch (error) {
    if (error instanceof FilePreviewError) {
      throw error;
    }

    // Handle network errors or other unexpected errors
    throw new FilePreviewError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while downloading K1X files",
    );
  }
};

// Cleanup cache every 10 minutes - only in browser environment
let cleanupIntervalId: NodeJS.Timeout | null = null;

export const startCleanupInterval = () => {
  if (cleanupIntervalId || typeof window === "undefined") return;
  cleanupIntervalId = setInterval(cleanupUrlCache, 10 * 60 * 1000);
};

export const stopCleanupInterval = () => {
  if (cleanupIntervalId) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
  }
};

// Start cleanup in browser environment
if (typeof window !== "undefined") {
  startCleanupInterval();
}
